// TanStack mutation hooks for every operation the QueryBuilder canvas
// performs. Each hook accepts `{ ruleId }` so it can invalidate the right
// `useRuleQuery` cache on settle.
//
// Conventions:
//   - All hooks toast the API error message on failure (no silent fails).
//   - "Settled" invalidates the rule cache, so the canonical server data
//     overwrites any local optimistic state.
//   - Optimistic updates are applied for text/value/operator edits (where
//     flicker is most visible) and for Data Fields reorder (see
//     `optimisticUpdates.js`). Other create/delete/reorder mutations let
//     the invalidate handle it.

import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "react-toastify";

import * as rulesService from "../../../services/rules";
import * as entityService from "../../../services/entity";
import { getAxiosResponseError } from "../../../utils/functions";

import {
  ruleQueryKey,
  displayedEntitiesQueryKey,
} from "./queryKeys";
import {
  setEntityName,
  setQueryAlias,
  setQueryLimit,
  patchConditionField,
  reorderDataFieldsInCache,
} from "./optimisticUpdates";

const toastError = (err) => toast.error(getAxiosResponseError(err));

// ── Internal helpers ─────────────────────────────────────────────────────────

// Standard mutation factory: invalidates the rule cache on settle, toasts the
// error on failure. Use for any mutation that doesn't need an optimistic update.
function useRuleSimpleMutation({ mutationFn, ruleId, alsoInvalidate = [] }) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn,
    onError: toastError,
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ruleQueryKey(ruleId) });
      alsoInvalidate.forEach((key) =>
        queryClient.invalidateQueries({ queryKey: key }),
      );
    },
  });
}

// Optimistic mutation factory: snapshots rule cache, applies a local patch
// before the request, rolls back on error, invalidates on settle so the
// server's view wins.
function useRuleOptimisticMutation({ mutationFn, ruleId, applyOptimistic }) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn,
    onMutate: async (vars) => {
      const key = ruleQueryKey(ruleId);
      await queryClient.cancelQueries({ queryKey: key });
      const previous = queryClient.getQueryData(key);
      if (previous) {
        queryClient.setQueryData(key, applyOptimistic(previous, vars));
      }
      return { previous };
    },
    onError: (err, _vars, ctx) => {
      if (ctx?.previous) {
        queryClient.setQueryData(ruleQueryKey(ruleId), ctx.previous);
      }
      toastError(err);
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ruleQueryKey(ruleId) });
    },
  });
}

// ── Entities ─────────────────────────────────────────────────────────────────

// Server expects `{ entityId, queryId }`.
export function useAttachEntityMutation({ ruleId }) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: rulesService.attachEntity,
    onError: toastError,
    onSettled: (_data, _err, vars) => {
      queryClient.invalidateQueries({ queryKey: ruleQueryKey(ruleId) });
      if (vars?.queryId) {
        queryClient.invalidateQueries({
          queryKey: displayedEntitiesQueryKey(vars.queryId),
        });
      }
    },
  });
}

// Service takes the bare displayedEntityId.
export function useDetachEntityMutation({ ruleId, queryId }) {
  return useRuleSimpleMutation({
    mutationFn: entityService.detachEntity,
    ruleId,
    alsoInvalidate: queryId ? [displayedEntitiesQueryKey(queryId)] : [],
  });
}

export function useExpandEntityMutation({ ruleId, queryId }) {
  return useRuleSimpleMutation({
    mutationFn: entityService.expandEntity,
    ruleId,
    alsoInvalidate: queryId ? [displayedEntitiesQueryKey(queryId)] : [],
  });
}

export function useShrinkEntityMutation({ ruleId, queryId }) {
  return useRuleSimpleMutation({
    mutationFn: entityService.shrinkEntity,
    ruleId,
    alsoInvalidate: queryId ? [displayedEntitiesQueryKey(queryId)] : [],
  });
}

export function useEditEntityNameMutation({ ruleId, queryId }) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: rulesService.editEntitiyName, // service typo preserved
    onMutate: async (vars) => {
      const key = ruleQueryKey(ruleId);
      await queryClient.cancelQueries({ queryKey: key });
      const previous = queryClient.getQueryData(key);
      if (previous) {
        queryClient.setQueryData(
          key,
          setEntityName(previous, vars.id, vars.name),
        );
      }
      return { previous };
    },
    onError: (err, _vars, ctx) => {
      if (ctx?.previous) {
        queryClient.setQueryData(ruleQueryKey(ruleId), ctx.previous);
      }
      toastError(err);
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ruleQueryKey(ruleId) });
      if (queryId) {
        queryClient.invalidateQueries({
          queryKey: displayedEntitiesQueryKey(queryId),
        });
      }
    },
  });
}

// ── Data fields ──────────────────────────────────────────────────────────────

export function usePostDataFieldMutation({ ruleId }) {
  return useRuleSimpleMutation({
    mutationFn: rulesService.postDataField,
    ruleId,
  });
}

export function useDeleteDataFieldMutation({ ruleId }) {
  return useRuleSimpleMutation({
    mutationFn: rulesService.deleteDataField,
    ruleId,
  });
}

export function useReorderDataFieldsMutation({ ruleId }) {
  // Optimistic: reordering is the one drag-and-drop outcome where the round
  // trip is very noticeable — the chip should land where it was dropped
  // immediately, not snap back and forth while the PUT settles.
  return useRuleOptimisticMutation({
    mutationFn: rulesService.reorderDataFields,
    ruleId,
    applyOptimistic: (rule, vars) => reorderDataFieldsInCache(rule, vars.fields),
  });
}

export function useEditFieldNameMutation({ ruleId }) {
  return useRuleOptimisticMutation({
    mutationFn: rulesService.editFieldName,
    ruleId,
    applyOptimistic: (rule, vars) =>
      patchConditionField(rule, vars.id, { name: vars.name }),
  });
}

// ── Conditions & groups ──────────────────────────────────────────────────────

export function usePostConditionGroupMutation({ ruleId }) {
  return useRuleSimpleMutation({
    mutationFn: rulesService.postConditionGroup,
    ruleId,
  });
}

export function useCreateFieldMutation({ ruleId }) {
  return useRuleSimpleMutation({
    mutationFn: rulesService.createField,
    ruleId,
  });
}

export function useUpdateFieldMutation({ ruleId }) {
  // Optimistic: the common updateField call is a value/operator/sign change,
  // a pure patch. Mirror it into the cache so the UI doesn't flicker between
  // keystrokes.
  return useRuleOptimisticMutation({
    mutationFn: rulesService.updateField,
    ruleId,
    applyOptimistic: (rule, vars) => patchConditionField(rule, vars.id, vars),
  });
}

export function useDeleteConditionGroupMutation({ ruleId }) {
  return useRuleSimpleMutation({
    mutationFn: rulesService.deleteConditionGroup,
    ruleId,
  });
}

export function useDeleteGroupMutation({ ruleId }) {
  return useRuleSimpleMutation({
    mutationFn: rulesService.deleteGroup,
    ruleId,
  });
}

export function useEditGroupMutation({ ruleId }) {
  return useRuleSimpleMutation({
    mutationFn: rulesService.editGroup,
    ruleId,
  });
}

export function useDeleteFieldMutation({ ruleId }) {
  return useRuleSimpleMutation({
    mutationFn: rulesService.deleteField,
    ruleId,
  });
}

// ── Field functions (SUM, AVG, custom) ───────────────────────────────────────

export function useAddConditionFunctionMutation({ ruleId }) {
  return useRuleSimpleMutation({
    mutationFn: rulesService.addConditionFunction,
    ruleId,
  });
}

export function useDeleteEntityFieldFunctionMutation({ ruleId }) {
  return useRuleSimpleMutation({
    mutationFn: rulesService.deleteEntityFieldFunction,
    ruleId,
  });
}

// ── Group by ─────────────────────────────────────────────────────────────────

export function usePostGroupByMutation({ ruleId }) {
  return useRuleSimpleMutation({
    mutationFn: rulesService.postGroupBy,
    ruleId,
  });
}

export function useDeleteGroupByMutation({ ruleId }) {
  return useRuleSimpleMutation({
    mutationFn: rulesService.deleteGroupBy,
    ruleId,
  });
}

// ── Order by ─────────────────────────────────────────────────────────────────

export function usePostOrderByMutation({ ruleId }) {
  return useRuleSimpleMutation({
    mutationFn: rulesService.postOrderBy,
    ruleId,
  });
}

export function useDeleteOrderByMutation({ ruleId }) {
  return useRuleSimpleMutation({
    mutationFn: rulesService.deleteOrderBy,
    ruleId,
  });
}

export function useReorderOrderByMutation({ ruleId }) {
  return useRuleSimpleMutation({
    mutationFn: rulesService.reorderOrderBy,
    ruleId,
  });
}

export function useUpdateOrderByMutation({ ruleId }) {
  return useRuleSimpleMutation({
    mutationFn: rulesService.updateOrderBy,
    ruleId,
  });
}

// ── Limit ────────────────────────────────────────────────────────────────────

export function usePostLimitMutation({ ruleId }) {
  return useRuleOptimisticMutation({
    mutationFn: rulesService.postLimit,
    ruleId,
    applyOptimistic: (rule, vars) =>
      setQueryLimit(rule, vars.queryId, vars.limit),
  });
}

export function useDeleteLimitMutation({ ruleId }) {
  return useRuleSimpleMutation({
    mutationFn: rulesService.deleteLimit,
    ruleId,
  });
}

// ── Query alias (name) ───────────────────────────────────────────────────────

export function useUpdateQueryNameMutation({ ruleId }) {
  return useRuleOptimisticMutation({
    mutationFn: rulesService.updateQueryName,
    ruleId,
    applyOptimistic: (rule, vars) =>
      setQueryAlias(rule, vars.queryId, vars.alias),
  });
}

// ── Sub-queries ──────────────────────────────────────────────────────────────

export function usePostSubQueryMutation({ ruleId }) {
  return useRuleSimpleMutation({
    mutationFn: rulesService.postSubQuery,
    ruleId,
  });
}

export function useDeleteSubQueryMutation({ ruleId }) {
  return useRuleSimpleMutation({
    mutationFn: rulesService.deleteSubQuery,
    ruleId,
  });
}
